import { useCallback, useMemo } from 'react';
import Select from 'react-select';
import {
  FaAngleDown,
} from "react-icons/fa";

const defaultSettingObject = {
  control: (provided, state) => ({
    ...provided,
    paddingLeft: '16px',
    paddingRight: '16px',
    width: '100%',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: '0.5px solid #888',
    boxShadow: 'none',
  }),
  container: (provided, state) => ({
    ...provided,
    height: '36px',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: 0,
  }),
  menu: (provided, state) => ({
    ...provided,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
    border: 'solid 0.5px #eaeaea',
    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.12)',
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: 0,
    maxHeight: "250px",
  }),
  option: (provided, state) => ({
    ...provided,
    marginRight: '12px',
    marginLeft: '12px',
    // Grow to fit long, wrapping labels (e.g. full hotel names) instead of a
    // fixed height that clips/overlaps the next row. Padding + line-height give
    // each option breathing room.
    minHeight: '36px',
    height: 'auto',
    paddingTop: '8px',
    paddingBottom: '8px',
    lineHeight: '1.3',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    borderRadius: '6px',
    color: '#000000DF',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#007aff14',
      color: '#007aff',
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const smallSelectStyling = {
  ...defaultSettingObject,
  menu: (provided, state) => ({
    ...provided,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
    width: '200px',
    border: 'solid 0.5px #eaeaea',
    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.12)',
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: 0,
    maxHeight: "250px",
    width: '200px',
  }),
  container: (provided, state) => ({
    ...provided,
    height: '36px',
    width: '185px',
  }),
}

// Like the default styling but the dropdown menu sizes to its content (wider
// than the narrow control) so long labels — e.g. full hotel names in the Add
// Trip pickup/dropoff pickers — stay on one or two lines instead of jumbling.
const wideSelectStyling = {
  ...defaultSettingObject,
  menu: (provided, state) => ({
    ...provided,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
    minWidth: '100%',
    width: 'max-content',
    maxWidth: '460px',
    border: 'solid 0.5px #eaeaea',
    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.12)',
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: 0,
    maxHeight: '250px',
  }),
};

const multiSelectStyling = {
  ...defaultSettingObject,
  control: (provided, state) => ({
    ...provided,
    paddingLeft: '8px',
    paddingRight: '8px',
    width: '100%',
    minHeight: '36px',
    height: 'auto',
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: '0.5px solid #888',
    boxShadow: 'none',
  }),
  container: (provided, state) => ({
    ...provided,
    width: '100%',
    minHeight: '36px',
    height: 'auto',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '2px 0',
    flexWrap: 'wrap',
  }),
  multiValue: (provided, state) => ({
    ...provided,
    margin: '2px 4px 2px 0',
    maxWidth: '100%',
  }),
  multiValueLabel: (provided, state) => ({
    ...provided,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
  }),
  menu: (provided, state) => ({
    ...provided,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
    minWidth: '320px',
    width: 'max-content',
    maxWidth: '480px',
    border: 'solid 0.5px #eaeaea',
    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.12)',
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: 0,
    maxHeight: '250px',
  }),
};

const smallMultiSelectStyling = {
  ...multiSelectStyling,
  container: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    height: 'auto',
    width: '185px',
  }),
  menu: (provided, state) => ({
    ...provided,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
    width: '200px',
    border: 'solid 0.5px #eaeaea',
    boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.12)',
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: 0,
    maxHeight: "250px",
    width: '200px',
  }),
};

const CustomSelect = ({
  id,
  options,
  value,
  onChange,
  size,
  isMulti,
}) => {
  const selectedOption = useMemo(() => {
    if (isMulti) {
      if (!value || !Array.isArray(value)) return [];
      return options.filter(_item => value.includes(_item.value));
    }
    return value ? options.find(_item => _item.value == value) : null
  }, [options, value, isMulti]);
  const onValueChange = useCallback((newValue) => {
    if (isMulti) {
      onChange(newValue ? newValue.map((v) => v.value) : []);
    } else {
      onChange(newValue.value);
    }
  }, [onChange, isMulti]);

  const getStyles = () => {
    if (isMulti) {
      return size === "small" ? smallMultiSelectStyling : multiSelectStyling;
    }
    if (size === "wide") return wideSelectStyling;
    return size === "small" ? smallSelectStyling : defaultSettingObject;
  };

  return (
    <Select
      instanceId={id ?? "custom-select"}
      styles={getStyles()}
      options={options}
      value={selectedOption}
      isSearchable={true}
      isMulti={isMulti || false}
      onChange={onValueChange}
      components={{
        DropdownIndicator: () => (
          <FaAngleDown />
        ),
        IndicatorSeparator: () => null,
      }}
      menuPortalTarget={window.document.body}
    />
  );
}

export default CustomSelect;